import { useState } from 'react';
import { add, SourceSystem, SourceSystemSchema } from '@/api/doc/source-system';
import { Button } from '@/components/ui/button';
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from '@/components/ui/sheet';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CirclePlus } from 'lucide-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';

export function AddSourceSystem() {
	const [isSheetOpen, setIsSheetOpen] = useState(false);
	const queryClient = useQueryClient();

	const form = useForm<SourceSystem>({
		resolver: zodResolver(SourceSystemSchema),
		defaultValues: {
			id: null,
			name: '',
			slug: '',
			deletedOn: null,
		},
		mode: 'all',
	});

	const { mutate } = useMutation({
		mutationFn: (sourceSystem: SourceSystem) => add(sourceSystem),
		onSuccess: () => {
			toast.success(`Success! The source system has been successfully added`, {
				dismissible: true,
			});
			setIsSheetOpen(false);
			queryClient.invalidateQueries({ queryKey: ['source-systems'] });
		},
		onError: () => {
			toast.error(`Error! The source system could not be added`, {
				dismissible: true,
			});
		},
	});
	const onSubmit: SubmitHandler<SourceSystem> = (values: SourceSystem) =>
		mutate(values);

	form.watch();

	return (
		<Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
			<SheetTrigger asChild>
				<Button>
					<CirclePlus />
					Add Source System
				</Button>
			</SheetTrigger>
			<SheetContent className="overflow-scroll">
				<SheetHeader>
					<SheetTitle>Add Source System</SheetTitle>
				</SheetHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
						<FormField
							control={form.control}
							name="name"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Source System Name</FormLabel>
									<FormControl>
										<Input {...field} placeholder="Source system name" />
									</FormControl>
									<FormDescription>Name the source system</FormDescription>
									<FormMessage />
								</FormItem>
							)}
						/>
						<Button
							type="submit"
							disabled={!form.formState.isValid || form.formState.isSubmitting}
						>
							{form.formState.isSubmitting ? 'Adding...' : 'Add'}
						</Button>
					</form>
				</Form>
			</SheetContent>
		</Sheet>
	);
}
